// Prerendered `/fields` — the habit tracker whose payoff is a causal graph over your own behavior.
// Node-prerender-safe and loader-free: read series.tsx's header for the rule at its source (the whole import graph is eagerly loaded in Node at prerender, so no store/Amplify/@shared/window anywhere under it). Chrome comes from splash-chrome and provides the ThemeProvider, so this file must NOT wrap `Mui`.
import { Link as RouterLink } from 'react-router';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { SplashChrome, AUTH_SIGN_UP } from './splash-chrome';
import { metaDescription, splashMeta, softwareApplicationLd, ORIGIN } from './series-ld';
import { Lanes, InputTypes, Causal, AskYourData, Outline, DatesAndFiles, Gamification, SeriesSeam } from './fields-sections';
import { Band, CtaBand } from './splash-sections';
import type { Route } from './+types/fields';

const TITLE = 'AI Habit Tracker with Causal Analysis — Gnothi';
const DESCRIPTION = metaDescription(
  'Track habits, dailies and todos in a Workflowy-style outline, then let causal AI show which of your behaviors actually drive the others. Free to start.',
);
const PATH = '/fields';

export const meta: Route.MetaFunction = () => [
  ...splashMeta({ title: TITLE, description: DESCRIPTION, path: PATH }),
  {
    'script:ld+json': softwareApplicationLd({
      name: 'Gnothi Fields',
      description: DESCRIPTION,
      url: `${ORIGIN}${PATH}`,
      applicationCategory: 'LifestyleApplication',
      featureList: [
        'Habit, daily, todo, reward and custom field lanes',
        'PCMCI+ causal analysis over your tracked behavior',
        'Plain-language Q&A over your own field data',
        'Keyboard-driven nested outline under every field',
        'Due dates and file attachments',
      ],
    }),
  },
];

function Hero() {
  return (
    <Band color="dark" hero>
      <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center', maxWidth: 820, mx: 'auto' }}>
        {/* `mb: 0` because the theme's `h2` carries a 1rem bottom margin (its `h3` did not), which would double up with the Stack's own spacing. The ramp is here because the theme's h1/h2 sizes are flat — no breakpoints — so an unramped 3rem headline is 48px on a 360px phone. */}
        <Typography variant="h2" component="h1" sx={{ fontWeight: 700, mb: 0, fontSize: { xs: '2.125rem', sm: '2.5rem', md: '3rem' } }}>
          Track the habit. Find out what actually moved it.
        </Typography>
        {/* `component="p"` keeps the contract's `variant="h6"` subhead size while removing a real <h6> from the outline — an h1 followed by an h6 skips four levels, and this line is a subhead, not a section. */}
        <Typography variant="h6" component="p" sx={{ fontWeight: 400, opacity: 0.9 }}>
          Gnothi Fields is an AI habit tracker whose payoff isn't a streak badge - it's a causal graph over your own behavior, showing which of the things you track are moving the others.
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.72, maxWidth: 620 }}>
          Every link it finds carries a direction, an effect size and a lag: which field moved which, how hard, and how long it took.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 1 }}>
          <Button component={RouterLink} to={AUTH_SIGN_UP} variant="contained" size="large" sx={{ px: 4 }}>
            Get started free
          </Button>
          <Button href="#causal" variant="outlined" color="inherit" size="large">
            See the causal graph
          </Button>
        </Stack>
      </Stack>
    </Band>
  );
}

export default function Fields() {
  return (
    <SplashChrome active="fields">
      <Hero />
      <Lanes />
      <InputTypes />
      <Causal />
      <AskYourData />
      <Outline />
      <DatesAndFiles />
      <Gamification />
      <SeriesSeam />
      <CtaBand
        color="light"
        title="Start tracking. The graph comes later."
        deck="Fields, outlines and logging are free. Log for a week or two and there'll be something worth analyzing."
      />
    </SplashChrome>
  );
}
